import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { EntitySelect } from '../../components/EntityField';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { enumOptions } from '../../lib/enums';
import {
  currencySymbol,
  formatMoney,
  type NumberInputValue,
  toMinorUnits,
  typedNumber,
} from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { type StockItem, useStockItems } from '../inventory/stock';

const IMPORT_PATH = '/admin/v1/suppliers/supplier-catalog-items/import';

/** Above this many rows nobody is reviewing anything, and the review is the whole safety of this. */
const MOST_ROWS = 200;

interface DraftRow {
  key: string;
  /** Exactly what was on the pasted line, kept so a person can see what a row came from. */
  source: string;
  /** Their own name or code for it, as pasted. */
  theirName: string;
  inventoryItemId: string;
  /** True when the name was matched automatically — worth showing, because it can be wrong. */
  guessed: boolean;
  packQty: NumberInputValue;
  price: NumberInputValue;
}

let seq = 0;

/**
 * Names as they compare: lower case, no punctuation, no double spaces. A distributor writes
 * "FLOR DE CANA 7 AÑOS 750ML" and the bar's own item is "Flor de Caña 7"; nothing here will match
 * those two, and nothing here pretends to. The match is a starting point a person corrects, and
 * every guessed row says it is a guess.
 */
function normalise(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function bestMatch(theirName: string, items: StockItem[]): StockItem | undefined {
  const needle = normalise(theirName);
  if (!needle) return undefined;
  const exact = items.find((i) => normalise(i.nameEn) === needle);
  if (exact) return exact;
  // One containing the other, longest first, so "Rum" does not win over "Flor de Caña 7 Rum".
  const contains = items
    .filter((i) => {
      const mine = normalise(i.nameEn);
      return mine.length > 2 && (needle.includes(mine) || mine.includes(needle));
    })
    .sort((a, b) => normalise(b.nameEn).length - normalise(a.nameEn).length);
  return contains[0];
}

/**
 * A pasted line, split into columns.
 *
 * Tabs first, because a spreadsheet or a PDF table pasted into a browser arrives tab-separated and
 * that is unambiguous. Then commas, then runs of two or more spaces — a single space is not a
 * column separator when half the rows are called "Flor de Caña 7 Años".
 */
function columnsOf(line: string): string[] {
  if (line.includes('\t')) return line.split('\t').map((c) => c.trim());
  if (line.includes(',')) return line.split(',').map((c) => c.trim());
  return line.split(/ {2,}/).map((c) => c.trim());
}

/** A cell that is only a number, with the thousands separators and money symbols people paste. */
function asNumber(cell: string): number | undefined {
  const cleaned = cell.replace(/[$L\s]/gi, '').replace(/,/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Turn a pasted block into rows to review.
 *
 * The guessing is deliberately shallow. The LAST number on a line is the price — every price list
 * ever printed puts it there — and a second number before it is the pack size. Everything that is
 * not a number is their name for the thing. Anything this gets wrong is visible and editable in the
 * table underneath, which is why it is allowed to guess at all.
 */
export function parsePastedList(text: string, items: StockItem[]): DraftRow[] {
  const rows: DraftRow[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const cells = columnsOf(line).filter((c) => c !== '');
    if (cells.length === 0) continue;

    const numbers: { at: number; value: number }[] = [];
    cells.forEach((cell, at) => {
      const value = asNumber(cell);
      if (value !== undefined) numbers.push({ at, value });
    });
    // A line with no number on it is a heading — "SPIRITS", "Effective 1 July" — not a price.
    if (numbers.length === 0) continue;

    const price = numbers[numbers.length - 1] as { at: number; value: number };
    const pack = numbers.length > 1 ? (numbers[numbers.length - 2] as { at: number; value: number }) : null;
    const theirName = cells
      .filter((_, at) => at !== price.at && at !== pack?.at)
      .join(' ')
      .trim();
    const matched = bestMatch(theirName, items);

    rows.push({
      key: `row-${++seq}`,
      source: line,
      theirName,
      inventoryItemId: matched?.id ?? '',
      guessed: Boolean(matched),
      packQty: pack && pack.value > 0 ? pack.value : 1,
      price: price.value,
    });
    if (rows.length >= MOST_ROWS) break;
  }
  return rows;
}

/**
 * A distributor's price list, in one go, checked by a person before it commits.
 *
 * A file-format parser would be the wrong build here. What actually arrives is a WhatsApp photo, a
 * PDF, or a table in an email, and all three end up as text somebody can select and paste — and the
 * assistant can already read a photographed document into the same shape. What matters is not the
 * format; it is that nothing is written until the owner has looked at every row beside the item it
 * will be recorded against.
 *
 * The rows a person has to check are exactly the ones the guess could not resolve, and they are
 * sorted to the top. Nothing commits while one is unresolved.
 */
export function ImportPriceListForm({
  supplierId,
  supplierName,
  currency: theirCurrency,
  onClose,
  onSaved,
}: {
  supplierId: string;
  supplierName: string;
  currency: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const stock = useStockItems();
  const items = [...stock.byId.values()];

  const [pasted, setPasted] = useState('');
  const [rows, setRows] = useState<DraftRow[] | null>(null);
  const [currency, setCurrency] = useState(theirCurrency);
  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const islandToday = useIslandToday();
  const [effectiveAt, setEffectiveAt] = useState(islandToday);
  const [error, setError] = useState<string | null>(null);

  const setRow = (key: string, patch: Partial<DraftRow>) =>
    setRows((current) => (current ?? []).map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const read = () => {
    const parsed = parsePastedList(pasted, items);
    if (parsed.length === 0) {
      return setError('Nothing on that looks like a price. Each line needs a name and a number.');
    }
    setError(null);
    // Unmatched first: those are the rows that need a person, and they must not be below the fold.
    setRows(
      [...parsed].sort((a, b) => Number(Boolean(a.inventoryItemId)) - Number(Boolean(b.inventoryItemId))),
    );
  };

  const usable = (rows ?? []).filter((r) => r.inventoryItemId);
  const unmatched = (rows ?? []).filter((r) => !r.inventoryItemId);
  const duplicated = new Set(
    usable.map((r) => r.inventoryItemId).filter((id, i, all) => all.indexOf(id) !== i),
  );

  const save = useMutation({
    mutationFn: () =>
      rawApi<{ applied: unknown[]; unchanged: number }>('POST', IMPORT_PATH, {
        supplierId,
        effectiveAt,
        currency,
        lines: usable.map((r) => ({
          inventoryItemId: r.inventoryItemId,
          packQty: Math.max(typedNumber(r.packQty) ?? 1, 0.000001),
          packUnit: stock.unitOf(r.inventoryItemId) || 'unit',
          priceMinor: toMinorUnits(r.price) ?? 0,
          description: r.theirName || null,
        })),
      }),
    onSuccess: (result) => {
      onSaved();
      onClose();
      const changed = result.applied.length;
      notifyDone(
        'Price list recorded',
        changed === 0
          ? `Nothing on that list has changed since the last one from ${supplierName}.`
          : `${changed} ${changed === 1 ? 'price' : 'prices'} recorded from ${supplierName}${
              result.unchanged > 0 ? `, ${result.unchanged} unchanged` : ''
            }. Every price they replace is kept.`,
      );
    },
    onError: (err: Error) => notifyError(err, 'record this price list'),
  });

  const submit = () => {
    if (usable.length === 0) return setError('Say what at least one of these lines is a price for');
    if (unmatched.length > 0) {
      return setError(
        `${unmatched.length} ${unmatched.length === 1 ? 'line has' : 'lines have'} nothing to record against — say what they are for, or take them off`,
      );
    }
    if (duplicated.size > 0) {
      return setError('The same stock item is on this list twice — put the whole price on one line');
    }
    const noPrice = usable.find((r) => (toMinorUnits(r.price) ?? 0) <= 0);
    if (noPrice) return setError(`Say what they charge for ${stock.nameOf(noPrice.inventoryItemId)}`);
    if (!effectiveAt) return setError('Say what day this list applies from');
    setError(null);
    save.mutate();
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={`Record a price list from ${supplierName}`}
      size="xl"
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {rows === null ? (
          <>
            <Text size="sm" c="dimmed">
              Paste their list — from a spreadsheet, an email, or typed off the sheet they left. One thing a
              line, with what they charge on it. Nothing is recorded until you have checked it.
            </Text>
            <Textarea
              label="Their price list"
              description="A name and a price on each line. If they quote by the case, put the case size before the price."
              autosize
              minRows={8}
              maxRows={18}
              placeholder={'Flor de Caña 7\t12\t2880.00\nSalva Vida\t24\t960.00\nCoca-Cola 355ml\t24\t480.00'}
              value={pasted}
              onChange={(e) => setPasted(e.currentTarget.value)}
            />
            {error && (
              <Text size="sm" c="red">
                {error}
              </Text>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={read} disabled={!pasted.trim()}>
                Read the list
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Group grow align="flex-start">
              <Select
                label="They quoted it in"
                required
                data={enumOptions('currency')}
                value={currency}
                onChange={(v) => setCurrency(v ?? theirCurrency)}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
                disabled={save.isPending}
              />
              <DateInput
                label="Priced from"
                description="The date on their sheet, so older orders stay priced as they were"
                required
                value={effectiveAt || null}
                onChange={(v) => setEffectiveAt(v ?? '')}
                valueFormat="D MMM YYYY"
                popoverProps={{ withinPortal: true }}
                disabled={save.isPending}
              />
            </Group>

            {unmatched.length > 0 && (
              <Alert color="yellow" variant="light" title="Some lines have nothing to record against">
                {unmatched.length} {unmatched.length === 1 ? 'line' : 'lines'} could not be matched to
                anything you hold. Say what {unmatched.length === 1 ? 'it is' : 'they are'} for, or take{' '}
                {unmatched.length === 1 ? 'it' : 'them'} off the list. Nothing is recorded until they are
                dealt with.
              </Alert>
            )}

            <Table.ScrollContainer minWidth={720}>
              <Table verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: '30%' }}>Their line</Table.Th>
                    <Table.Th style={{ width: '30%' }}>What it is</Table.Th>
                    <Table.Th style={{ width: 120 }}>How many</Table.Th>
                    <Table.Th style={{ width: 160 }}>What that costs</Table.Th>
                    <Table.Th ta="right">Each</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((row) => {
                    const unit = stock.unitOf(row.inventoryItemId);
                    const pack = Math.max(typedNumber(row.packQty) ?? 1, 1);
                    const priceMinor = toMinorUnits(row.price) ?? 0;
                    return (
                      <Table.Tr key={row.key}>
                        <Table.Td>
                          <Text size="sm">{row.theirName || '—'}</Text>
                          {/* What was actually on the line, so a mis-split column is visible
                              rather than being something the reader has to infer. */}
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {row.source}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <EntitySelect
                            entity="inventoryItem"
                            value={row.inventoryItemId || null}
                            onChange={(v) => setRow(row.key, { inventoryItemId: v ?? '', guessed: false })}
                            disabled={save.isPending}
                          />
                          {row.guessed && row.inventoryItemId && (
                            <Badge size="xs" variant="light" color="blue" mt={4}>
                              Matched by name — check it
                            </Badge>
                          )}
                          {duplicated.has(row.inventoryItemId) && (
                            <Badge size="xs" variant="light" color="red" mt={4}>
                              On this list twice
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <NumberInput
                            aria-label="How many they sell it in"
                            min={1}
                            suffix={unit ? ` ${unit}` : undefined}
                            value={row.packQty}
                            onChange={(v) => setRow(row.key, { packQty: v })}
                            disabled={save.isPending}
                          />
                        </Table.Td>
                        <Table.Td>
                          <NumberInput
                            aria-label="What that costs"
                            min={0}
                            prefix={`${currencySymbol(currency)} `}
                            decimalScale={2}
                            thousandSeparator=","
                            hideControls
                            value={row.price}
                            onChange={(v) => setRow(row.key, { price: v })}
                            disabled={save.isPending}
                          />
                        </Table.Td>
                        <Table.Td ta="right">
                          {priceMinor > 0 ? (
                            <Text size="sm" fw={600}>
                              {formatMoney(Math.round(priceMinor / pack), currency)}
                            </Text>
                          ) : (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td ta="right">
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="red"
                            onClick={() => setRows((cur) => (cur ?? []).filter((r) => r.key !== row.key))}
                            disabled={save.isPending}
                          >
                            Take off
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <Text size="xs" c="dimmed">
              A price that matches what is already on file writes nothing — the same list next month records
              only what has changed. Every price this replaces is kept and readable.
            </Text>

            {error && (
              <Text size="sm" c="red">
                {error}
              </Text>
            )}

            <Group justify="space-between">
              <Button variant="subtle" onClick={() => setRows(null)} disabled={save.isPending}>
                Back to the paste
              </Button>
              <Group>
                <Button variant="default" onClick={onClose} disabled={save.isPending}>
                  Cancel
                </Button>
                <Button loading={save.isPending} onClick={submit}>
                  Record {usable.length} {usable.length === 1 ? 'price' : 'prices'}
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
